import { wrapCoord, toroidalDistanceSq } from '../../../../lib/simulation-core'
import type { Vec2 } from '../../../../lib/simulation-core'
import type { BraitenbergWorld, VehicleConfig, VehicleState } from './types'

export const MAX_SUBSTEP_DISTANCE = 2
export const MAX_SUBSTEP_ROTATION = 0.1

export interface VehicleSubstepObservation {
  vehicleIndex: number
  dt: number
  from: Vec2
  to: Vec2
  headingBefore: number
  headingAfter: number
}

export interface StepWorldObserver {
  onVehicleSubstep?: (observation: VehicleSubstepObservation) => void
}

function sensorWorldPosition(
  vehicle: VehicleState,
  offsetX: number,
  offsetY: number,
): Vec2 {
  const cos = Math.cos(vehicle.heading)
  const sin = Math.sin(vehicle.heading)
  return {
    x: vehicle.position.x + offsetX * cos - offsetY * sin,
    y: vehicle.position.y + offsetX * sin + offsetY * cos,
  }
}

function computeStimuli(
  vehicle: VehicleState,
  config: VehicleConfig,
  world: Pick<BraitenbergWorld, 'sources' | 'bounds'>,
): number[] {
  const stimuli: number[] = []

  for (let si = 0; si < config.sensors.length; si++) {
    const sensor = config.sensors[si]
    const sensorPos = sensorWorldPosition(
      vehicle,
      sensor.offset.x,
      sensor.offset.y,
    )

    let total = 0
    for (const source of world.sources) {
      const distSq = toroidalDistanceSq(sensorPos, source.position, world.bounds)
      total += source.intensity / (distSq + 1)
    }

    stimuli.push(total)
  }

  return stimuli
}

function computeMotorOutputs(
  config: VehicleConfig,
  stimuli: number[],
): { leftMotor: number; rightMotor: number } {
  let leftMotor = 0
  let rightMotor = 0

  for (const conn of config.connections) {
    const output = conn.response(stimuli[conn.sensorIndex])
    if (conn.motorIndex === 0) {
      leftMotor += output
    } else {
      rightMotor += output
    }
  }

  return { leftMotor, rightMotor }
}

function updateTelemetry(
  vehicle: VehicleState,
  config: VehicleConfig,
  stimuli: number[],
  leftMotor: number,
  rightMotor: number,
) {
  vehicle.sensorStimuli = stimuli
  vehicle.primaryStimulus = stimuli[config.connections[0]?.sensorIndex ?? 0] ?? 0
  vehicle.leftMotor = leftMotor
  vehicle.rightMotor = rightMotor
}

export function stepWorld(
  world: BraitenbergWorld,
  configs: VehicleConfig[],
  dt: number,
  rng: () => number = Math.random,
  observer?: StepWorldObserver,
): void {
  const { vehicles, sources, bounds, trailLength } = world

  for (let vi = 0; vi < vehicles.length; vi++) {
    const vehicle = vehicles[vi]
    const config = configs[vi]

    const brownianTurnRate = config.brownianNoise * (rng() - 0.5)
    let remainingDt = dt

    while (remainingDt > 0) {
      const stimuli = computeStimuli(vehicle, config, { sources, bounds })
      const { leftMotor, rightMotor } = computeMotorOutputs(config, stimuli)
      updateTelemetry(vehicle, config, stimuli, leftMotor, rightMotor)

      const linearVelocity =
        config.driveMode === 'single-forward'
          ? leftMotor
          : (leftMotor + rightMotor) / 2

      // World convention is x-right / y-down with heading increasing clockwise.
      // With that convention, left > right must rotate clockwise (turn right),
      // so the correct sign is (left - right) / wheelBase.
      const angularVelocity =
        config.driveMode === 'single-forward'
          ? 0
          : (leftMotor - rightMotor) / config.wheelBase

      const turnRate = angularVelocity + brownianTurnRate
      const translationLimitedDt =
        Math.abs(linearVelocity) > 0
          ? MAX_SUBSTEP_DISTANCE / Math.abs(linearVelocity)
          : remainingDt
      const rotationLimitedDt =
        Math.abs(turnRate) > 0
          ? MAX_SUBSTEP_ROTATION / Math.abs(turnRate)
          : remainingDt

      let substepDt = Math.min(remainingDt, translationLimitedDt, rotationLimitedDt)
      if (!Number.isFinite(substepDt) || substepDt <= 0) {
        substepDt = remainingDt
      }

      const from = { ...vehicle.position }
      const headingBefore = vehicle.heading

      vehicle.heading += turnRate * substepDt
      vehicle.position.x += linearVelocity * Math.cos(vehicle.heading) * substepDt
      vehicle.position.y += linearVelocity * Math.sin(vehicle.heading) * substepDt

      vehicle.position.x = wrapCoord(vehicle.position.x, bounds.width)
      vehicle.position.y = wrapCoord(vehicle.position.y, bounds.height)

      observer?.onVehicleSubstep?.({
        vehicleIndex: vi,
        dt: substepDt,
        from,
        to: { ...vehicle.position },
        headingBefore,
        headingAfter: vehicle.heading,
      })

      remainingDt -= substepDt
      if (remainingDt < 1e-9) {
        remainingDt = 0
      }
    }

    vehicle.trail.push({ x: vehicle.position.x, y: vehicle.position.y })
    if (vehicle.trail.length > trailLength) {
      vehicle.trail.splice(0, vehicle.trail.length - trailLength)
    }
  }
}
